import type { LocationQueryValue, RouteLocationRaw, RouteParamsRaw, Router } from 'vue-router';

import { isFullUrl } from '@/core/composables/UseParsedUri';
import ApiError from '@/core/lib/ApiError';
import { RouteName } from '@/core/router/RouteName';
import { AuthenticationError } from '@/domains/authentification/lib/Authentication';

export class RouteError extends Error {}
export class NotFoundRouteError extends RouteError {}
export class AccessDeniedRouteError extends RouteError {}

export const WILDCARD_PATH = '/:pathMatch(.*)*';

const ERROR_ROUTE_NAMES = new Set<string>([
  RouteName.ERROR,
  RouteName.ERROR_ACCESS_DENIED,
  RouteName.ERROR_NOT_FOUND,
]);

export function isProjectRoute(router: Router, path: string): boolean {
  const resolved = router.resolve(path);

  return (
    resolved.matched.length > 0
    && resolved.name != null
    && !ERROR_ROUTE_NAMES.has(resolved.name as string)
  );
}

export async function navigateToAccessPath(
  router: Router,
  accessPath: string | undefined,
  urlExterne?: boolean,
  onError?: () => void,
): Promise<void> {
  if (!accessPath) return;

  if (urlExterne || isFullUrl(accessPath)) {
    window.open(accessPath, '_blank', 'noopener,noreferrer');
  } else if (isProjectRoute(router, accessPath)) {
    await router.push(accessPath);
  } else {
    onError?.();

    throw new Error(`Le chemin d'accès "${accessPath}" n'est pas valide.`);
  }
}

export function convertMaybeErrorToRouteError(maybeError: unknown): RouteError {
  // Api errors
  if (maybeError instanceof ApiError) {
    if (maybeError.status === 404) {
      return new NotFoundRouteError(maybeError.message);
    } else if (maybeError.status === 401 || maybeError.status === 403) {
      return new AccessDeniedRouteError(maybeError.message);
    } else {
      return new RouteError(maybeError.message);
    }
  }

  // Auth errors
  if (maybeError instanceof AuthenticationError) {
    return new AccessDeniedRouteError(maybeError.message);
  }

  // Other possible cases
  let msg: string | undefined = undefined;

  if (maybeError instanceof Error) {
    msg = maybeError.message;
  } else if (typeof maybeError === 'string') {
    msg = maybeError;
  }

  return new RouteError(msg);
}

export function getRouteNameFromMaybeError(maybeError: unknown): RouteLocationRaw {
  if (maybeError instanceof AccessDeniedRouteError) {
    return { name: RouteName.ERROR_ACCESS_DENIED };
  }

  if (maybeError instanceof NotFoundRouteError) {
    return { name: RouteName.ERROR_NOT_FOUND };
  }

  return { name: RouteName.ERROR };
}

export function ensureQueryParamArray(val?: LocationQueryValue | LocationQueryValue[]): LocationQueryValue[] {
  if (typeof val === 'string' && val.length > 0) {
    return [val];
  }

  if (Array.isArray(val)) {
    return val;
  }

  return [];
}

export function cleanQueryParams(queryParams: RouteParamsRaw): RouteParamsRaw {
  return Object.entries(queryParams).reduce<RouteParamsRaw>((acc, [key, val]) => {
    if (val === undefined || val === null || (Array.isArray(val) && val.length === 0)) {
      return acc;
    }

    if (Array.isArray(val) && val.length === 1) {
      acc[key] = val.at(0);
    } else {
      acc[key] = val;
    }

    return acc;
  }, {});
}
